import {
  Button,
  DatePickerDropdown,
  HSpacer,
  InfoTooltip,
  Input,
  MenuItem,
  Modal,
  NumericInput,
  Select,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import { ApiCategoryMapping } from '@api/interfaces/ApiCategoryMapping';
import { ApiEnergyProgramDetail } from '@api/interfaces/ApiEnergyProgramDetail';
import { Stack } from '@mui/material';
import { RewardsProgramType } from '@shared/enums';
import { formatLocalDate } from '@shared/utilities';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EnergyProgramForm,
  EnergyProgramPeriod,
  isEnergyProgramFormComplete,
  validateEnergyProgramForm,
} from './energyValidation';

interface EnergyProgramModalProps {
  apiError?: string | null,
  categoryMappings: ApiCategoryMapping[],
  defaultStartDate?: string,
  existingProgram?: ApiEnergyProgramDetail,
  isSaving?: boolean,
  onClose: () => void,
  onSave: (form: Required<EnergyProgramForm>) => void,
  otherEnergyPrograms?: EnergyProgramPeriod[],
}

const FIELD_WIDTH = 464;

// The picker shows a Date in the browser's zone, so a stored day maps to local midnight.
const toJsDate = (date: string | null) => (date ? DateTime.fromISO(date).toJSDate() : undefined);

export const EnergyProgramModal = ({
  apiError,
  categoryMappings,
  defaultStartDate,
  existingProgram,
  isSaving = false,
  onClose,
  onSave,
  otherEnergyPrograms = [],
}: EnergyProgramModalProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<EnergyProgramForm>({
    basePointsPerGallon: existingProgram?.basePointsPerGallon ?? undefined,
    categoryMappingId: existingProgram?.categoryMapping?.id ?? null,
    endDate: existingProgram?.endDate ?? null,
    name: existingProgram?.name ?? t(($) => $.energy.programModal.defaultName),
    pointsExpirationDate: existingProgram?.pointsExpirationDate ?? null,
    startDate: existingProgram?.startDate ?? defaultStartDate ?? null,
  });
  const errors = validateEnergyProgramForm(form, otherEnergyPrograms);
  const errorText = (field: keyof EnergyProgramForm) => (
    errors[field] ? t(($) => $.energy.programModal.errors[errors[field]!]) : undefined
  );
  const update = (patch: Partial<EnergyProgramForm>) => setForm({ ...form, ...patch });
  const isRateLocked = !!existingProgram?.hasEnergyPoints;
  const isPeriodLocked = (existingProgram?.memberCount ?? 0) > 0;
  const isMappingLockedWhileReady = !isRateLocked && !!existingProgram?.isReady;
  // A mapping belongs to one program, so offer only unassigned ones and this program's own.
  const availableMappings = categoryMappings.filter(({ programId, programType }) => (
    programType === RewardsProgramType.Energy
    && (!programId || programId === existingProgram?.id)
  ));

  return (
    <Modal
      acceptButton={(props) => (
        <Button
          {...props}
          disabled={!isEnergyProgramFormComplete(form, otherEnergyPrograms) || isSaving}
          loading={isSaving}
          onClick={() => onSave(form as Required<EnergyProgramForm>)}
          testID="energy-program-save"
        >
          {existingProgram
            ? t(($) => $.common.actions.save)
            : t(($) => $.energy.programModal.configureTiers)}
        </Button>
      )}
      cancelButton={(props) => (
        <Button {...props} onClick={onClose} testID="energy-program-cancel">
          {t(($) => $.common.actions.cancel)}
        </Button>
      )}
      largeModal
      onClose={onClose}
      open
      testID="energy-program-modal"
      title={existingProgram
        ? t(($) => $.energy.programModal.editTitle)
        : t(($) => $.energy.programModal.addTitle)}
    >
      {!!apiError && (
        <>
          <Text category="body-medium" sx={{ color: 'error.main' }} testID="energy-program-error">
            {apiError}
          </Text>
          <VSpacer size="5" />
        </>
      )}
      <Text category="body-large">
        {t(($) => $.energy.programModal.intro)}
      </Text>
      <VSpacer size="8" />
      <Text category="title-medium">
        {t(($) => $.energy.programModal.nameLabel)}
      </Text>
      <VSpacer size="5" />
      <Input
        error={!!errors.name}
        helperText={errorText('name')}
        label={t(($) => $.energy.programModal.nameLabel)}
        onChangeText={(name) => update({ name })}
        testID="energy-program-name"
        value={form.name}
        width={FIELD_WIDTH}
      />
      <VSpacer size="9" />
      <Text category="title-medium">
        {t(($) => $.energy.programModal.periodLabel)}
      </Text>
      <VSpacer size="6" />
      {isPeriodLocked ? (
        <>
          <Stack data-testid="energy-program-period-locked" direction="row">
            {[
              {
                date: form.startDate,
                key: 'start',
                label: t(($) => $.energy.programModal.startDateLabel),
              },
              {
                date: form.endDate,
                key: 'end',
                label: t(($) => $.energy.programModal.endDateLabel),
              },
            ].map(({ date, key, label }) => (
              <Stack key={key} sx={{ mr: '32px' }}>
                <Text category="label-large">{label}</Text>
                <VSpacer size="2" />
                <Text category="body-large" testID={`energy-program-${key}-text`}>
                  {t(($) => $.energy.programModal.dateValue, { date })}
                </Text>
              </Stack>
            ))}
          </Stack>
          <VSpacer size="2" />
          <Text category="body-small" sx={{ color: 'text.secondary' }}>
            {t(($) => $.energy.programModal.periodLocked)}
          </Text>
        </>
      ) : (
        <Stack direction="row" width={FIELD_WIDTH}>
          <DatePickerDropdown
            errorMessage={errorText('startDate')}
            label={t(($) => $.energy.programModal.startDateLabel)}
            onChange={(date) => update({ startDate: date ? formatLocalDate(date) : null })}
            testID="energy-program-start"
            value={toJsDate(form.startDate)}
          />
          <HSpacer size="7" />
          <DatePickerDropdown
            errorMessage={errorText('endDate')}
            label={t(($) => $.energy.programModal.endDateLabel)}
            onChange={(date) => update({ endDate: date ? formatLocalDate(date) : null })}
            testID="energy-program-end"
            value={toJsDate(form.endDate)}
          />
        </Stack>
      )}
      <VSpacer size="6" />
      <Stack alignItems="center" direction="row">
        <Text category="title-medium">
          {t(($) => $.energy.programModal.expirationLabel)}
        </Text>
        <HSpacer size="3" />
        <InfoTooltip>
          {t(($) => $.energy.programModal.expirationHint)}
        </InfoTooltip>
      </Stack>
      <VSpacer size="6" />
      <DatePickerDropdown
        errorMessage={errorText('pointsExpirationDate')}
        label={t(($) => $.energy.programModal.expirationLabel)}
        onChange={(date) => update({ pointsExpirationDate: date ? formatLocalDate(date) : null })}
        testID="energy-program-expiration"
        value={toJsDate(form.pointsExpirationDate)}
        width={FIELD_WIDTH}
      />
      <VSpacer size="9" />
      <Text category="title-medium">
        {t(($) => $.energy.programModal.baseRateLabel)}
      </Text>
      <VSpacer size="5" />
      <NumericInput
        decimals={4}
        disabled={isRateLocked}
        error={form.basePointsPerGallon !== undefined && !!errors.basePointsPerGallon}
        helperText={isRateLocked
          ? t(($) => $.energy.tiers.ratesLocked)
          : form.basePointsPerGallon !== undefined && errors.basePointsPerGallon
            ? errorText('basePointsPerGallon')
            : t(($) => $.energy.programModal.baseRateHint)}
        label={t(($) => $.energy.programModal.baseRateLabel)}
        onChangeNumber={(basePointsPerGallon) => update({ basePointsPerGallon })}
        placeholder={t(($) => $.energy.programModal.baseRatePlaceholder)}
        testID="energy-program-base-rate"
        value={form.basePointsPerGallon}
        width={FIELD_WIDTH}
      />
      <VSpacer size="9" />
      <Text category="title-medium">
        {t(($) => $.energy.programModal.categoryMappingLabel)}
      </Text>
      <VSpacer size="5" />
      <Select
        disabled={isRateLocked || isMappingLockedWhileReady}
        label={t(($) => $.energy.programModal.categoryMappingLabel)}
        onChangeValue={(value) => update({ categoryMappingId: value || null })}
        testID="energy-program-category-mapping"
        value={form.categoryMappingId ?? ''}
        width={FIELD_WIDTH}
      >
        <MenuItem testID="energy-program-category-mapping-none" value="">
          {t(($) => $.energy.programModal.categoryMappingNone)}
        </MenuItem>
        {availableMappings.map((mapping) => (
          <MenuItem
            key={mapping.id}
            testID={`energy-program-category-mapping-${mapping.id}`}
            value={mapping.id}
          >
            {mapping.name}
          </MenuItem>
        ))}
      </Select>
      <VSpacer size="2" />
      <Text category="body-small" sx={{ color: 'text.secondary' }}>
        {isRateLocked && t(($) => $.energy.programModal.categoryMappingLocked)}
        {isMappingLockedWhileReady
          && t(($) => $.energy.programModal.categoryMappingLockedWhileReady)}
        {!isRateLocked && !isMappingLockedWhileReady
          && t(($) => $.energy.programModal.categoryMappingHint)}
      </Text>
      {!existingProgram && (
        <>
          <VSpacer size="6" />
          <Text category="body-medium" sx={{ color: 'text.secondary' }}>
            {t(($) => $.energy.programModal.configureTiersHint)}
          </Text>
        </>
      )}
    </Modal>
  );
};
